// class BookingCycleMgr will keep the booking cycle state, it will keep information about the current booking cycle, and it will keep the booking cycle state.
// Properties: new booking, pending booking, current booking, completed booking, cancelled booking, and the booking cycle state.
// Page refresh will reset the booking cycle state. Booking cycle needs to be reset when the user logs out. Booking cycle needs to discover page refresh.

// class Booking will keep the booking information.

export class Booking {
	date: Date;
	status:
		| "pending"
		| "completed"
		| "cancelled"
		| "loading"
		| "unknown"
		| "unloaded"
		| "noise";
	type: "manual" | "call" | "unknown" | "callMeBack";
	accountCode: number;
	jobId: number;
	sessionId: string;
	eventId: number;
	callId: string; // New: Call ID support

	constructor() {
		this.date = new Date();
		this.status = "unknown";
		this.type = "unknown";
		this.accountCode = 0;
		this.jobId = 0;
		this.sessionId = "";
		this.eventId = 0;
		this.callId = ""; // New: Initialize call ID
	}

	// Booking will be reset when the user logs out.
	reset() {
		this.date = new Date();
		this.status = "unknown";
		this.type = "unknown";
		this.accountCode = 0;
		this.jobId = 0;
	}

	// Booking will be set when new booking is started.
	setBooking(
		bookingType: "manual" | "call" | "unknown" | "callMeBack",
		bookingAccountCode: number,
		bookingJobId: number,
		status?:
			| "pending"
			| "completed"
			| "cancelled"
			| "loading"
			| "unknown"
			| "unloaded"
			| "noise",
		sessionId?: string,
		eventId?: number,
	) {
		this.date = new Date();
		this.status = status || "unknown";
		this.type = bookingType;
		this.accountCode = bookingAccountCode;
		this.jobId = bookingJobId;
		this.sessionId = sessionId || "";
		this.eventId = eventId || 0;
	}

	// Booking will be set when booking is completed.
	setBookingCompleted() {
		this.status = "completed";
	}

	// Booking will be set when booking is cancelled.
	setBookingCancelled() {
		this.status = "cancelled";
	}

	// Booking will be set when booking is marked as noise.
	setBookingNoise() {
		this.status = "noise";
	}

	// Booking will be set when booking is pending.
	setBookingPending() {
		this.status = "pending";
	}

	// Booking will be set when booking is not started.
	setBookingLoading() {
		this.status = "loading";
	}

	// Booking will be set when booking is unknown.
	setBookingUnknown() {
		this.status = "unknown";
	}

	// Booking will be set when booking is unloaded.
	setBookingUnloaded() {
		this.status = "unloaded";
	}

	// Booking job id
	getBookingJobId() {
		return this.jobId;
	}

	// Booking account code
	getBookingAccountCode() {
		return this.accountCode;
	}

	// set booking job id
	setBookingJobId(bookingJobId: number) {
		this.jobId = bookingJobId;
	}

	// set booking account code
	setBookingAccountCode(bookingAccountCode: number) {
		this.accountCode = bookingAccountCode;
	}
}

export class BookingCycleMgr {
	private bookingMgr: { [key: number]: Booking } = {};
	private currentBooking: Booking | null = null;

	constructor() {
		this.bookingMgr = {};
		this.currentBooking = null;
		this.load();
	}

	// getCurrentBooking will return the current booking.
	getCurrentBooking() {
		return this.currentBooking;
	}

	// Booking cycle mgr will save to local storage.
	save() {
		localStorage.setItem("bookingMgr", JSON.stringify(this.bookingMgr));
		localStorage.setItem(
			"bookingMgrCurrent",
			JSON.stringify(this.currentBooking),
		);
	}

	// Booking cycle mgr will load from local storage.
	load() {
		const bookingMgr = localStorage.getItem("bookingMgr");

		if (bookingMgr && bookingMgr !== "undefined" && bookingMgr !== "null") {
			const mgr = JSON.parse(bookingMgr);
			for (const key in mgr) {
				const booking = new Booking();
				booking.setBooking(
					mgr[key].status,
					mgr[key].accountCode,
					mgr[key].bookingJobId,
					mgr[key].status,
				);
				booking.type = mgr[key].type;
				booking.date = new Date(mgr[key].date);
				booking.sessionId = mgr[key].sessionId;
				booking.eventId = mgr[key].eventId;
				booking.callId = mgr[key].callId || ""; // New: Load call ID
				this.bookingMgr[Number(key)] = booking;
			}
			const currentBooking = localStorage.getItem("bookingMgrCurrent");
			if (currentBooking) {
				const booking = JSON.parse(currentBooking) as Booking;
				if (booking) {
					this.currentBooking = new Booking();
					this.currentBooking.setBooking(
						booking.type,
						booking.accountCode,
						booking.jobId,
						booking.status,
					);
					this.currentBooking.type = booking.type;
					this.currentBooking.date = new Date(booking.date);
					this.currentBooking.sessionId = booking.sessionId;
					this.currentBooking.eventId = booking.eventId;
					this.currentBooking.callId = booking.callId || ""; // New: Load call ID
				}
			}
		}
	}
	// Booking cycle will be set when new booking is started.
	setBooking(
		bookingType: "manual" | "call" | "callMeBack" | "unknown",
		bookingAccountCode: number,
		bookingJobId: number,
	) {
		const booking = new Booking();
		booking.setBooking(bookingType, bookingAccountCode, bookingJobId);
		this.bookingMgr[bookingJobId] = booking;
		this.currentBooking = booking;
		this.save();
	}

	// Booking cycle will be set when booking is completed.
	setBookingCompleted(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			booking.setBookingCompleted();
		}
		this.save();
	}

	// Set sessionId for the current booking.
	setCurrentBookingSessionId(sessionId: string) {
		if (this.currentBooking) {
			this.currentBooking.sessionId = sessionId;
		}
		this.save();
	}

	// Set eventId for the current booking.
	setCurrentBookingEventId(eventId: number) {
		if (this.currentBooking) {
			this.currentBooking.eventId = eventId;
		}
		this.save();
	}

	// getSessionId will return the session id for the current booking.
	getCurrentBookingSessionId() {
		if (this.currentBooking) {
			return this.currentBooking.sessionId;
		}
		return "";
	}

	// getEventId will return the event id for the current booking.
	getCurrentBookingEventId() {
		if (this.currentBooking) {
			return this.currentBooking.eventId;
		}
		return 0;
	}

	// is there a booking with sessionId?
	hasSessionId(sessionId: string) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].sessionId === sessionId) {
				return true;
			}
		}
		return false;
	}

	// is there a booking with eventId?
	hasEventId(eventId: number) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].eventId === eventId) {
				return true;
			}
		}
		return false;
	}

	resetSessionId(sessionId: string) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].sessionId === sessionId) {
				this.bookingMgr[key].sessionId = "";
			}
		}
		this.save();
	}

	resetEventId(eventId: number) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].eventId === eventId) {
				this.bookingMgr[key].eventId = 0;
			}
		}
		this.save();
	}

	// Set current booking to job id.
	setCurrentBooking(jobId: number, source?: string) {
		const booking = this.bookingMgr[jobId];
		if (booking) {
			this.currentBooking = booking;
		} else {
			this.setBooking("unknown", 0, jobId);
		}
		this.save();
	}

	// Booking cycle will be set when booking is cancelled.
	setBookingCancelled(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			booking.setBookingCancelled();
		}
		this.save();
	}

	// Booking cycle will be set when booking is pending.
	setBookingPending(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			booking.setBookingPending();
		}
		this.save();
	}

	// Booking cycle will be set when booking is not started.
	setBookingLoading(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			booking.setBookingLoading();
		}
		this.save();
	}

	// Booking cycle will be set when booking is unknown.
	setBookingUnknown(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			booking.setBookingUnknown();
		}
		this.save();
	}

	// Get the booking information.
	getBooking(bookingJobId: number) {
		return this.bookingMgr[bookingJobId];
	}

	// isBookingCompleted will return true if the booking is completed.
	isBookingCompleted(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "completed";
		}
		return false;
	}

	// isBookingCancelled will return true if the booking is cancelled.
	isBookingCancelled(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "cancelled";
		}
		return false;
	}

	// isBookingPending will return true if the booking is pending.
	isBookingPending(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "pending";
		}
		return false;
	}

	// isBookingNoise will return true if the booking is noise.
	isBookingNoise(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "noise";
		}
		return false;
	}

	// isBookingLoading will return true if the booking is loading.
	isBookingLoading(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "loading";
		}
		return false;
	}

	// isBookingUnknown will return true if the booking is unknown.
	isBookingUnknown(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status === "unknown";
		}
		return false;
	}

	// isBooking will return true if the booking exists.
	isBooking(bookingJobId: number) {
		return this.bookingMgr[bookingJobId] !== undefined;
	}

	// isBooking will return true if the booking is started.
	isBookingStarted(bookingJobId: number) {
		const booking = this.bookingMgr[bookingJobId];
		if (booking) {
			return booking.status !== "unknown";
		}
		return false;
	}

	// set current booking status to unknown.
	setCurrentBookingUnknown() {
		if (this.currentBooking) {
			this.currentBooking.setBookingUnknown();
		}
		this.save();
	}

	// set current booking status to unknown.
	setCurrentBookingUnloaded() {
		if (this.currentBooking) {
			this.currentBooking.setBookingUnloaded();
		}
		this.save();
	}

	// set current booking status to completed.
	setCurrentBookingCompleted() {
		if (this.currentBooking) {
			this.currentBooking.setBookingCompleted();
		}
		this.save();
	}

	// set current booking status to cancelled.
	setCurrentBookingCancelled() {
		if (this.currentBooking) {
			this.currentBooking.setBookingCancelled();
		}
		this.save();
	}

	// set current booking status to noise.
	setCurrentBookingNoise() {
		if (this.currentBooking) {
			this.currentBooking.setBookingNoise();
		}
		this.save();
	}

	// set current booking status to pending.
	setCurrentBookingPending() {
		if (this.currentBooking) {
			this.currentBooking.setBookingPending();
		}
		this.save();
	}

	// set current booking status to loading.
	setCurrentBookingLoading() {
		if (this.currentBooking) {
			this.currentBooking.setBookingLoading();
		}
		this.save();
	}

	// is current booking status completed.
	isCurrentBookingCompleted() {
		if (this.currentBooking) {
			return this.currentBooking.status === "completed";
		}
		return false;
	}

	// is current booking status cancelled.
	isCurrentBookingCancelled() {
		if (this.currentBooking) {
			return this.currentBooking.status === "cancelled";
		}
		return false;
	}

	// is current booking status pending.
	isCurrentBookingPending() {
		if (this.currentBooking) {
			return this.currentBooking.status === "pending";
		}
		return false;
	}

	// is current booking status loading.
	isCurrentBookingLoading() {
		if (this.currentBooking) {
			return this.currentBooking.status === "loading";
		}
		return false;
	}

	// is current booking status unknown.
	isCurrentBookingUnknown() {
		if (this.currentBooking) {
			return this.currentBooking.status === "unknown";
		}
		return false;
	}

	// set current booking account code.
	setCurrentBookingAccountCode(accountCode: number) {
		if (this.currentBooking) {
			this.currentBooking.accountCode = accountCode;
		}
		this.save();
	}

	// get current booking account code.
	getCurrentBookingAccountCode() {
		if (this.currentBooking) {
			return this.currentBooking.accountCode;
		}
		return 0;
	}

	// set current booking job id, and replace the key for the current booking.
	setCurrentBookingJobId(jobId: number) {
		if (this.currentBooking) {
			this.bookingMgr[jobId] = this.currentBooking;
			this.currentBooking.jobId = jobId;
			delete this.bookingMgr[0];
		}

		this.save();
	}

	// get current booking job id.
	getCurrentBookingJobId() {
		if (this.currentBooking) {
			return this.currentBooking.jobId;
		}
		return 0;
	}

	// get current booking status.
	getCurrentBookingStatus() {
		if (this.currentBooking) {
			return this.currentBooking.status;
		}
		return "unknown";
	}

	// get current booking type.
	getCurrentBookingType() {
		if (this.currentBooking) {
			return this.currentBooking.type;
		}
		return "unknown";
	}

	// reset current booking.
	resetCurrentBooking() {
		this.currentBooking = null;
		this.save();
	}

	// ===== NEW: Call ID Support Methods =====

	/** Set callId for the current booking */
	setCurrentBookingCallId(callId: string) {
		if (this.currentBooking) {
			this.currentBooking.callId = callId;
		}
		this.save();
	}

	/** Get callId for the current booking */
	getCurrentBookingCallId() {
		if (this.currentBooking) {
			return this.currentBooking.callId;
		}
		return "";
	}

	/** Check if there is a booking with this call ID */
	hasCallId(callId: string) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].callId === callId) {
				return true;
			}
		}
		return false;
	}

	/** Reset call ID for bookings that match */
	resetCallId(callId: string) {
		for (const key in this.bookingMgr) {
			if (this.bookingMgr[key].callId === callId) {
				this.bookingMgr[key].callId = "";
			}
		}
		this.save();
	}

	/** Clear all call ID state */
	clearCallIdState() {
		for (const key in this.bookingMgr) {
			this.bookingMgr[key].callId = "";
		}
		if (this.currentBooking) {
			this.currentBooking.callId = "";
		}
		this.save();
	}
}

export const bookingCycleMgr = new BookingCycleMgr();

declare global {
	interface Window {
		bookingCycleMgr: BookingCycleMgr;
	}
}

window.bookingCycleMgr = bookingCycleMgr;

export default bookingCycleMgr;
